import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { Button, Form, IconButton } from '@onefootprint/ui';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
export type OutputMapping = {
  fieldKey: string;
  onboardingDataKey: string;
};

/** Consuming forms must keep their mappings under this field name. */
type OutputMappingsFormData = {
  outputs: OutputMapping[];
};
type OutputMappingsProps = {
  canEdit: boolean;
  subtitle: string;
  /** The outputs this node can emit. Each can only be mapped once. */
  options: {
    value: string;
    label: string;
  }[];
  outputLabel: string;
  outputAddon?: string;
  emptyMessage?: string;
  /** Field keys the node must always map, so their row can't be repointed or removed. */
  pinnedFieldKeys?: string[];
};
const OutputMappings = t0 => {
  const $ = _c(41);
  const {
    canEdit,
    subtitle,
    options,
    outputLabel,
    outputAddon,
    emptyMessage,
    pinnedFieldKeys: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const pinnedFieldKeys = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "nodeOutputs"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    t
  } = useTranslation("playbook-builder", t3);
  const {
    control
  } = useFormContext();
  let t4;
  if ($[3] !== control) {
    t4 = {
      control,
      name: "outputs"
    };
    $[3] = control;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    fields,
    append,
    remove
  } = useFieldArray(t4);
  const currentOutputs = useWatch({
    control,
    name: "outputs"
  }) ?? [];
  const usedFieldKeys = new Set(currentOutputs.map(_temp).filter(Boolean));
  const hasUnmappedOutputs = options.some(option => !usedFieldKeys.has(option.value));
  let t5;
  if ($[5] !== append) {
    t5 = () => {
      append({
        fieldKey: "",
        onboardingDataKey: ""
      });
    };
    $[5] = append;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleAddOutput = t5;
  let t6;
  if ($[7] !== t) {
    t6 = t(_temp2);
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <div className="text-label-2 text-primary">{t6}</div>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== subtitle) {
    t8 = <div className="text-body-2 text-tertiary">{subtitle}</div>;
    $[11] = subtitle;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <div className="flex flex-col gap-1">{t7}{t8}</div>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== emptyMessage || $[17] !== options.length) {
    t10 = options.length === 0 && emptyMessage && <div className="text-body-2 text-tertiary">{emptyMessage}</div>;
    $[16] = emptyMessage;
    $[17] = options.length;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== canEdit || $[20] !== currentOutputs || $[21] !== fields || $[22] !== options || $[23] !== outputAddon || $[24] !== outputLabel || $[25] !== pinnedFieldKeys || $[26] !== remove || $[27] !== t) {
    t11 = fields.length > 0 && <div className="flex flex-col gap-5 md:gap-3">{fields.map((field, index) => {
        const otherFieldKeys = new Set(currentOutputs.filter((_, i) => i !== index).map(_temp3));
        const isPinned = pinnedFieldKeys.includes(currentOutputs[index]?.fieldKey);
        return <div key={field.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 items-center md:grid-cols-[1fr_auto_1fr_auto] md:gap-4"><div className="col-span-full md:contents"><Form.Select name={`outputs.${index}.fieldKey`} aria-label={outputLabel} placeholder={t(_temp4)} addon={outputAddon} options={options.filter(option_0 => !otherFieldKeys.has(option_0.value))} disabled={!canEdit || isPinned} size="compact" rules={{
              required: t(_temp5)
            }} /></div><IcoArrowRightSmall16 className="flex-shrink-0" /><Form.Input name={`outputs.${index}.onboardingDataKey`} placeholder={t(_temp6)} addon="data." disabled={!canEdit} size="compact" rules={{
            required: t(_temp7)
          }} />{canEdit ? <IconButton icon={IcoTrash16} size="compact" variant="ghost" destructive={true} disabled={isPinned} onClick={() => remove(index)} aria-label={t(_temp8)} /> : <div className="w-4" />}</div>;
      })}</div>;
    $[19] = canEdit;
    $[20] = currentOutputs;
    $[21] = fields;
    $[22] = options;
    $[23] = outputAddon;
    $[24] = outputLabel;
    $[25] = pinnedFieldKeys;
    $[26] = remove;
    $[27] = t;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== canEdit || $[30] !== handleAddOutput || $[31] !== hasUnmappedOutputs || $[32] !== t) {
    t12 = canEdit && <div><Button fullWidth={false} variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} onClick={handleAddOutput} disabled={!hasUnmappedOutputs}>{t(_temp9)}</Button></div>;
    $[29] = canEdit;
    $[30] = handleAddOutput;
    $[31] = hasUnmappedOutputs;
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12) {
    t13 = <div className="flex flex-col gap-4 p-3 border border-tertiary rounded-md">{t10}{t11}{t12}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t13 || $[39] !== t9) {
    t14 = <section className="flex flex-col gap-3">{t9}{t13}</section>;
    $[38] = t13;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
};
export default OutputMappings;
function _temp(output) {
  return output?.fieldKey;
}
function _temp2($) {
  return $.title;
}
function _temp3(output_0) {
  return output_0?.fieldKey;
}
function _temp4($_0) {
  return $_0.output.placeholder;
}
function _temp5($_1) {
  return $_1.output.required;
}
function _temp6($_2) {
  return $_2.dataKey.placeholder;
}
function _temp7($_3) {
  return $_3.dataKey.required;
}
function _temp8($_4) {
  return $_4.remove;
}
function _temp9($_5) {
  return $_5.addOutput;
}